import { EntityType } from '@frontend/common';
import { FunctionalityType } from '@frontend/functionality/types';
import { PinCode, PinCodeType } from '@frontend/pin-code/types';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { TransactionClient, TransactionNivalizExtendedClient } from '@frontend/transaction/api';
import { ExtendedNivalizTransaction, TransactionStateName } from '@frontend/transaction/types';
import { useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useMenuRepository } from '../../../menu/menu-repository';
import { getNivalizCorporateTitle, getNivalizTypeLabel } from '../../../utils/nivaliz-transaction-labels';

interface ViewProps {
    navigate: NavigateFunction;
    isLoading: boolean;
    transaction: ExtendedNivalizTransaction | null;
    transactionTitle: React.ReactNode;
    typeLabel: React.ReactNode;
    pinCode: PinCode | null;
    deleteModal: boolean;
    setDeleteModal: (value: boolean) => void;
    onDelete: (confirmed: boolean) => void;
    onContinue?: () => void;
    showPinCodeModal: boolean;
    setShowPinCodeModal: (value: boolean) => void;
}

const useTransactionDetail = (): ViewProps => {
    const navigate = useNavigate();
    const location = useLocation();
    const { transactionId } = useParams();
    const menuRepo = useMenuRepository();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [showPinCodeModal, setShowPinCodeModal] = useState<boolean>(false);
    const [transaction, setTransaction] = useState<ExtendedNivalizTransaction | null>(null);
    const [continueInfo, setContinueInfo] = useState<{ wizardId: string; functionalityId: string } | null>(null);

    useEffect(() => {
        if (!transactionId) return;
        setIsLoading(true);
        TransactionClient.resolveTransactions([transactionId])
            .then((resolved) => {
                const entity = resolved.results[0];
                if (!entity) {
                    setTransaction(null);
                    return;
                }
                return TransactionNivalizExtendedClient.fetchExtendedNivalizTransaction(entity.account_id, entity.id).then((t) => setTransaction(t || null));
            })
            .catch(() => setTransaction(null))
            .finally(() => setIsLoading(false));
    }, [transactionId]);

    useEffect(() => {
        if (!isLoading && !transaction) {
            navigate(-1);
        }
    }, [isLoading, transaction, navigate]);

    useEffect(() => {
        if (!transaction || transaction.state !== TransactionStateName.TRANSACTION_CREATED || !transaction.application || !transaction.feature) {
            setContinueInfo(null);
            return;
        }

        const functionality = menuRepo
            .getFunctionalitiesByApplication(transaction.application, FunctionalityType.CORPORATE)
            .find((f) => f.feature === transaction.feature);
        if (!functionality) return;

        if (functionality.wizard_id) {
            setContinueInfo({ wizardId: functionality.wizard_id, functionalityId: functionality.id });
            return;
        }

        SettingClient.fetchSettings({ entity_id: functionality.id, entity_type: EntityType.FUNCTIONALITY }).then((settings) => {
            const setting = settings.results.find((s) => s.key === SettingKey.WIZARD_ID);
            if (setting && typeof setting.value === 'string') {
                setContinueInfo({ wizardId: setting.value, functionalityId: functionality.id });
            }
        });
    }, [transaction, menuRepo]);

    const pinCode = useMemo(() => {
        if (!transaction?.pin_codes?.length) return null;

        let expectedType: PinCodeType | null = null;
        if (transaction.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING) {
            expectedType = PinCodeType.PICK_UP;
        } else if (transaction.state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
            expectedType = PinCodeType.DROP_OFF;
        }
        if (!expectedType) return null;

        const match = transaction.pin_codes.find((pc: any) => pc && typeof pc === 'object' && 'type' in pc && String(pc.type) === String(expectedType));
        return (match as PinCode) || null;
    }, [transaction]);

    const transactionTitle = useMemo(() => {
        if (!transaction) return null;
        return getNivalizCorporateTitle({
            deceasedName: transaction.deceased?.name,
            trackingNumber: transaction.tracking_number,
            feature: transaction.feature,
            senderName: transaction.senders?.[0]?.name,
            receiverName: transaction.receivers?.[0]?.name
        });
    }, [transaction]);

    const onDelete = (confirmed: boolean) => {
        if (confirmed && transaction) {
            TransactionClient.deleteTransaction(transaction.account_id, transaction.id);
            navigate(-1);
        }
        setDeleteModal(false);
    };

    const onContinue = continueInfo
        ? () => {
              navigate(`/wizard/${continueInfo.functionalityId}/${continueInfo.wizardId}?from=${encodeURIComponent(location.pathname + location.search)}`, {
                  state: { transaction }
              });
          }
        : undefined;

    return {
        navigate,
        isLoading,
        transaction,
        transactionTitle,
        typeLabel: getNivalizTypeLabel(transaction?.feature),
        pinCode,
        deleteModal,
        setDeleteModal,
        onDelete,
        onContinue,
        showPinCodeModal,
        setShowPinCodeModal
    };
};

export default useTransactionDetail;
